export type Me = { id: number; username: string; is_admin: boolean };
export type Conversation = {
  id: number;
  title: string;
  claude_session_id: string | null;
  created_at: string;
  updated_at: string;
};
export type Message = {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
};
export type AdminUser = { id: number; username: string; is_admin: number; created_at: string };
export type ConversationsPage = { items: Conversation[]; total: number; limit: number; offset: number };

export type PendingQuote = {
  id: number;
  name: string;
  state: string;
  amount_total: number;
  currency: string | null;
  partner: string | null;
  date_order: string | false;
  activity_summary: string | false;
  activity_deadline: string | false;
};
export type QuoteLine = {
  id: number;
  name: string;
  qty: number;
  price_unit: number;
  subtotal: number;
  product: string | null;
};
export type QuoteAddress = { id: number; name: string; lines: string[] };
export type QuoteDetail = {
  id: number;
  name: string;
  state: string;
  web_url: string;
  partner: string | null;
  partner_email: string | null;
  invoice_address: QuoteAddress | null;
  shipping_address: QuoteAddress | null;
  same_address: boolean;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  currency: string | null;
  date_order: string | false;
  client_order_ref: string | false;
  lines: QuoteLine[];
};
export type ValidateResult = { ok: true; id: number; name: string; emailed: boolean; warning: string | null };

// --- Commandes (sale.order confirmées) ---
export type PendingOrder = {
  id: number;
  name: string;
  partner: string | null;
  amount_total: number;
  currency: string | null;
  date_order: string | false;
  delivery_status: string | false;
  invoice_status: string;
};
export type DeliverLine = { move_id: number; product: string | null; ordered: number; available: number };
export type OrderDetail = {
  id: number;
  name: string;
  state: string;
  stage: number;
  delivery_status: string | false;
  invoice_status: string;
  web_url: string;
  partner: string | null;
  partner_email: string | null;
  invoice_address: QuoteAddress | null;
  shipping_address: QuoteAddress | null;
  same_address: boolean;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  currency: string | null;
  date_order: string | false;
  client_order_ref: string | false;
  picking_name: string | null;
  deliver_lines: DeliverLine[];
  lines: QuoteLine[];
};
export type ValidateOrderResult = {
  ok: true;
  id: number;
  name: string;
  delivered: boolean;
  backorder: boolean;
  invoice_id: number | null;
  invoice_name: string | null;
};

// --- Factures (account.move out_invoice) ---
export type PendingInvoice = {
  id: number;
  name: string;
  partner: string | null;
  amount_total: number;
  amount_residual: number;
  currency: string | null;
  state: string;
  payment_state: string;
  invoice_date: string | false;
  invoice_date_due: string | false;
  invoice_origin: string | false;
  overdue: boolean;
};
export type InvoiceDetail = {
  id: number;
  name: string;
  state: string;
  payment_state: string;
  overdue: boolean;
  stage: number;
  web_url: string;
  partner: string | null;
  partner_email: string | null;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  amount_residual: number;
  currency: string | null;
  invoice_date: string | false;
  invoice_date_due: string | false;
  invoice_origin: string | false;
  client_ref: string;
  lines: QuoteLine[];
};
// --- Suivi unifié (une "affaire" par sale.order) ---
export type DealRow = {
  id: number;
  name: string;
  partner: string | null;
  amount_total: number;
  currency: string | null;
  stage: number;
  next_label: string;
  overdue: boolean;
  late: boolean;
};
export type DealLine = {
  id: number;
  name: string;
  qty: number;
  price_unit: number;
  subtotal: number;
  product: string | null;
  available: number;
  shortage: boolean;
};
export type DealNext = {
  kind: 'validate_quote' | 'validate_order' | 'post_invoice' | 'invoice_actions' | 'done';
  label: string;
};
export type DealInvoice = {
  id: number;
  name: string;
  state: string;
  payment_state: string;
  amount_residual: number;
  invoice_date_due: string | false;
  overdue: boolean;
  web_url: string;
};
export type DealDetail = {
  id: number;
  name: string;
  order_state: string;
  stage: number;
  next: DealNext;
  late: boolean;
  expected_date: string | false;
  stock_shortage: boolean;
  doc_key: string;
  web_url: string;
  partner: string | null;
  partner_email: string | null;
  invoice_address: QuoteAddress | null;
  shipping_address: QuoteAddress | null;
  same_address: boolean;
  date_order: string | false;
  client_order_ref: string | false;
  delivery_status: string | false;
  invoice_status: string;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  currency: string | null;
  invoice: DealInvoice | null;
  lines: DealLine[];
};

export type PostInvoiceResult = { ok: true; id: number; name: string | null; state: string };
export type SendInvoiceResult = { ok: true; id: number; emailed: boolean };
export type PayInvoiceResult = { ok: true; id: number; payment_state: string };

export type LogEntry = {
  id: number;
  created_at: string;
  source: string | null;
  level: 'error' | 'warning' | 'info';
  workflow: string | null;
  node: string | null;
  title: string | null;
  message: string | null;
  context: unknown | null;
  execution_url: string | null;
  resolved: boolean;
};
export type LogsResponse = { openCount: number; items: LogEntry[] };

export type ControlSummary = {
  devisAValider: number;
  commandesATraiter: number;
  commandesEnRetard: number;
  facturesAValider: number;
  facturesImpayees: number;
  achatsEnRetard: number;
  stockRuptures: number;
};

export type DashboardSummary = {
  currency: string;
  revenue: { today: number; week: number; month: number; year: number };
  pendingQuotes: number;
  receivables: { total: number; count: number };
  criticalProducts: number;
};
export type SalesReport = {
  currency: string;
  series: { month: string; revenue: number }[];
  topProducts: { name: string; revenue: number; qty: number }[];
  topCustomers: { name: string; revenue: number }[];
  margin: { total: number; percent: number };
};
export type StockReport = {
  belowMin: { name: string; onHand: number; min: number; forecast: number }[];
  outOfStock: { name: string; onHand: number; forecast: number }[];
  outOfStockCount: number;
};

export type StockStatus = 'rupture' | 'below_min' | 'overstock' | 'ok';
export type StockItem = {
  id: number;
  name: string;
  code: string;
  on_hand: number;
  reserved: number;
  free: number;
  incoming: number;
  forecast: number;
  min: number;
  max: number;
  status: StockStatus;
  /** Quantité vendue (commandes confirmées) — clé de tri principale. */
  sold: number;
};
export type StockProducts = {
  counts: { total: number; capped: boolean; rupture: number; below_min: number; overstock: number; reserved: number; in_stock: number };
  items: StockItem[];
};

export type ProductLot = {
  id: number; name: string; qty: number; reserved: number;
  expiration: string | null; expired: boolean;
};
export type ProductDetail = {
  product: {
    id: number; name: string; code: string; barcode: string | null;
    category: string | null; uom: string | null; tracking: string;
    use_expiration_date: boolean; expiration_time: number;
    sale_price: number; cost_price: number;
    on_hand: number; reserved: number; free: number; incoming: number; forecast: number;
    min: number; max: number; status: StockStatus; active: boolean;
  };
  lots: ProductLot[];
  stock: { location: string; lot: string | null; qty: number; reserved: number }[];
  movements: {
    date: string | null; qty: number; lot: string | null; lot_id: number | null;
    sens: 'in' | 'out' | 'interne'; picking: string | null;
    partner: string | null; origin: string | null;
  }[];
};

// --- Traçabilité par lot (rappel produit) ---
export type LotRow = {
  id: number;
  name: string;
  product: { id: number; name: string } | null;
  expiration: string | null;
  qty: number;
  created: string | null;
  expired: boolean;
  expiring: boolean;
};
export type LotsList = {
  counts: { total: number; capped: boolean; en_stock: number; perime: number; bientot: number };
  items: LotRow[];
};
export type LotPartner = {
  id: number; name: string; email: string | null; phone: string | null;
  city: string | null; country: string | null;
};
export type LotMovement = {
  qty: number; date: string | null; picking: string | null; picking_id: number | null;
  origin: string | null; partner: LotPartner | null; reference: string | null;
};
export type LotTrace = {
  lot: {
    id: number; name: string; product: { id: number; name: string } | null;
    expiration: string | null; removal: string | null; qty: number;
    created: string | null; expired: boolean; ref: string | null;
  };
  receptions: LotMovement[];
  livraisons: LotMovement[];
  stock: { location: string; qty: number; reserved: number }[];
  clients: (LotPartner & { qty: number; dates: string[] })[];
};

// --- Achats : factures fournisseurs (account.move in_invoice) ---
export type ReceiptState = 'received' | 'partial' | 'service' | 'missing';
export type PurchaseRow = {
  id: number;
  name: string;
  ref: string;
  partner: string | null;
  amount_total: number;
  amount_residual: number;
  currency: string | null;
  state: string;
  payment_state: string;
  invoice_date: string | false;
  invoice_origin: string | false;
  receipt: ReceiptState;
};
export type PurchaseLine = {
  id: number;
  name: string;
  product: string | null;
  qty: number;
  price_unit: number;
  subtotal: number;
};
export type PurchaseReception = { id: number; name: string; state: string };
export type PurchaseDetail = {
  id: number;
  name: string;
  ref: string;
  state: string;
  payment_state: string;
  receipt: ReceiptState;
  web_url: string;
  partner: string | null;
  invoice_date: string | false;
  invoice_date_due: string | false;
  invoice_origin: string | false;
  amount_untaxed: number;
  amount_tax: number;
  amount_total: number;
  amount_residual: number;
  currency: string | null;
  receptions: PurchaseReception[];
  lines: PurchaseLine[];
};

async function j<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status} ${body || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // Auth
  me: () => fetch('/api/auth/me', { credentials: 'include' }).then(j<Me>),
  login: (username: string, password: string) =>
    fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    }).then(j<Me>),
  logout: () => fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).then(j),

  // Conversations
  listConversations: (opts?: { limit?: number; offset?: number; q?: string }) => {
    const p = new URLSearchParams();
    if (opts?.limit != null) p.set('limit', String(opts.limit));
    if (opts?.offset != null) p.set('offset', String(opts.offset));
    if (opts?.q) p.set('q', opts.q);
    const qs = p.toString();
    return fetch(`/api/conversations${qs ? '?' + qs : ''}`, { credentials: 'include' }).then(j<ConversationsPage>);
  },
  createConversation: () =>
    fetch('/api/conversations', { method: 'POST', credentials: 'include' }).then(j<Conversation>),
  // Find-or-create the conversation tied to an Odoo document (e.g. "sale.order:195").
  openDocConversation: (docKey: string, title?: string) =>
    fetch('/api/conversations/for-doc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ docKey, title }),
    }).then(j<{ conversation: Conversation; existing: boolean }>),
  renameConversation: (id: number, title: string) =>
    fetch(`/api/conversations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title }),
    }).then(j),
  deleteConversation: (id: number) =>
    fetch(`/api/conversations/${id}`, { method: 'DELETE', credentials: 'include' }).then(j),
  listMessages: (id: number) =>
    fetch(`/api/conversations/${id}/messages`, { credentials: 'include' }).then(j<Message[]>),
  uploadAttachments: (convId: number, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('file', f, f.name);
    return fetch(`/api/conversations/${convId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    }).then(j<{ attachments: { name: string; size: number }[] }>);
  },

  // Admin
  listUsers: () => fetch('/api/admin/users', { credentials: 'include' }).then(j<AdminUser[]>),
  createUser: (username: string, password: string, is_admin: boolean) =>
    fetch('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password, is_admin }),
    }).then(j),
  resetPassword: (id: number, password: string) =>
    fetch(`/api/admin/users/${id}/password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    }).then(j),
  deleteUser: (id: number) =>
    fetch(`/api/admin/users/${id}`, { method: 'DELETE', credentials: 'include' }).then(j),

  // Devis (Odoo)
  listPendingQuotes: () => fetch('/api/quotes/pending', { credentials: 'include' }).then(j<PendingQuote[]>),
  getQuote: (id: number) => fetch(`/api/quotes/${id}`, { credentials: 'include' }).then(j<QuoteDetail>),
  validateQuote: (id: number) =>
    fetch(`/api/quotes/${id}/validate`, { method: 'POST', credentials: 'include' }).then(j<ValidateResult>),

  // Commandes (Odoo)
  listPendingOrders: () => fetch('/api/orders/pending', { credentials: 'include' }).then(j<PendingOrder[]>),
  getOrder: (id: number) => fetch(`/api/orders/${id}`, { credentials: 'include' }).then(j<OrderDetail>),
  validateOrder: (id: number, deliveries?: { move_id: number; qty: number }[]) =>
    fetch(`/api/orders/${id}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(deliveries ? { deliveries } : {}),
    }).then(j<ValidateOrderResult>),

  // Factures (Odoo)
  listInvoices: () => fetch('/api/invoices/pending', { credentials: 'include' }).then(j<PendingInvoice[]>),
  getInvoice: (id: number) => fetch(`/api/invoices/${id}`, { credentials: 'include' }).then(j<InvoiceDetail>),
  postInvoice: (id: number) =>
    fetch(`/api/invoices/${id}/post`, { method: 'POST', credentials: 'include' }).then(j<PostInvoiceResult>),
  sendInvoice: (id: number) =>
    fetch(`/api/invoices/${id}/send`, { method: 'POST', credentials: 'include' }).then(j<SendInvoiceResult>),
  payInvoice: (id: number) =>
    fetch(`/api/invoices/${id}/pay`, { method: 'POST', credentials: 'include' }).then(j<PayInvoiceResult>),

  // Suivi unifié (Odoo)
  getPipeline: () => fetch('/api/sales/pipeline', { credentials: 'include' }).then(j<DealRow[]>),
  getDeal: (id: number) => fetch(`/api/sales/${id}`, { credentials: 'include' }).then(j<DealDetail>),

  // Centre de contrôle
  getControlSummary: () => fetch('/api/control/summary', { credentials: 'include' }).then(j<ControlSummary>),

  // Journal technique
  getLogs: (opts?: { scope?: string; level?: string }) => {
    const p = new URLSearchParams();
    if (opts?.scope) p.set('scope', opts.scope);
    if (opts?.level) p.set('level', opts.level);
    const qs = p.toString();
    return fetch(`/api/logs${qs ? '?' + qs : ''}`, { credentials: 'include' }).then(j<LogsResponse>);
  },
  resolveLog: (id: number, resolved: boolean) =>
    fetch(`/api/logs/${id}/resolve`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ resolved }),
    }).then(j<{ ok: true; id: number; resolved: boolean }>),

  // Dashboards (Odoo aggregates)
  getDashboardSummary: () => fetch('/api/dashboards/summary', { credentials: 'include' }).then(j<DashboardSummary>),
  getSalesReport: () => fetch('/api/dashboards/sales', { credentials: 'include' }).then(j<SalesReport>),
  getStockReport: () => fetch('/api/dashboards/stock', { credentials: 'include' }).then(j<StockReport>),

  // Achats (Odoo)
  listPurchases: () => fetch('/api/purchases/pending', { credentials: 'include' }).then(j<PurchaseRow[]>),
  getPurchase: (id: number) => fetch(`/api/purchases/${id}`, { credentials: 'include' }).then(j<PurchaseDetail>),

  // Écran Stocks (opérationnel)
  getStockProducts: (opts?: { q?: string; status?: string }) => {
    const p = new URLSearchParams();
    if (opts?.q) p.set('q', opts.q);
    if (opts?.status && opts.status !== 'all') p.set('status', opts.status);
    const qs = p.toString();
    return fetch(`/api/stock/products${qs ? '?' + qs : ''}`, { credentials: 'include' }).then(j<StockProducts>);
  },

  getProduct: (id: number) =>
    fetch(`/api/stock/products/${id}`, { credentials: 'include' }).then(j<ProductDetail>),

  getLots: (opts?: { q?: string; filter?: string }) => {
    const p = new URLSearchParams();
    if (opts?.q) p.set('q', opts.q);
    if (opts?.filter && opts.filter !== 'all') p.set('filter', opts.filter);
    const qs = p.toString();
    return fetch(`/api/lots${qs ? '?' + qs : ''}`, { credentials: 'include' }).then(j<LotsList>);
  },

  getLot: (id: number) =>
    fetch(`/api/lots/${id}`, { credentials: 'include' }).then(j<LotTrace>),
};

export type StreamEvent =
  | { kind: 'session'; sessionId: string }
  | { kind: 'text'; delta: string }
  | { kind: 'tool_use'; name: string; input: unknown }
  | { kind: 'tool_result'; ok: boolean }
  | { kind: 'done'; usage?: unknown }
  | { kind: 'error'; message: string };

export type SseEvent =
  | { event: 'session' | 'text' | 'tool_use' | 'tool_result' | 'done' | 'title' | 'error' | 'end'; data: any };

/**
 * POST a message and read the SSE stream. Calls `onEvent` per event,
 * resolves when the server sends `event: end` or the stream closes.
 */
export async function postMessageStream(
  convId: number,
  content: string,
  onEvent: (ev: SseEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(`/api/conversations/${convId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ content }),
    signal,
  });
  if (!res.ok || !res.body) {
    throw new Error(`HTTP ${res.status}`);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buf += dec.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const lines = chunk.split('\n');
      let evType: string = 'message';
      const dataLines: string[] = [];
      for (const line of lines) {
        if (line.startsWith('event: ')) evType = line.slice(7).trim();
        else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
      }
      if (dataLines.length === 0) continue;
      let data: any;
      try {
        data = JSON.parse(dataLines.join('\n'));
      } catch {
        continue;
      }
      onEvent({ event: evType as any, data });
    }
  }
}
